import type { QuestionPublic } from "@/client"
import { stripOptionPrefix } from "@/utils/text-format"
import { Box, Stack, Text } from "@chakra-ui/react"
import { HtmlContent } from "../Common/HtmlContent"

interface QuestionDisplayProps {
  question: QuestionPublic
}

/**
 * Simple read-only question display component for viewing question details
 * Used in modals and previews where answers/rationale shouldn't be shown
 */
export function QuestionDisplay({ question }: QuestionDisplayProps) {
  return (
    <Stack gap={4}>
      {/* Question Text */}
      <Box>
        <Text fontSize="sm" fontWeight="medium" color="fg.muted" mb={2}>
          Question:
        </Text>
        <HtmlContent
          content={question.question_text}
          css={{
            "& p": { marginBottom: "0.5rem" },
            "& ul, & ol": { marginLeft: "1.5rem", marginBottom: "0.5rem" },
          }}
        />
      </Box>

      {/* Options */}
      <Box>
        <Text fontSize="sm" fontWeight="medium" color="fg.muted" mb={2}>
          Options:
        </Text>
        <Stack gap={2}>
          {question.options.map((option: string, index: number) => (
            <Box
              key={index}
              p={3}
              borderWidth="1px"
              borderRadius="md"
              bg="bg.muted"
            >
              <Box display="flex" gap={2}>
                <Text>{String.fromCharCode(65 + index)}.</Text>
                <HtmlContent content={stripOptionPrefix(option)} />
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Metadata */}
      {(question.subjects.length > 0 || question.topics.length > 0) && (
        <Box>
          <Text fontSize="sm" color="fg.muted">
            {question.subjects.length > 0 && (
              <span>
                Subject{question.subjects.length > 1 ? "s" : ""}:{" "}
                {question.subjects.map((s) => s.name).join(", ")}
              </span>
            )}
            {question.subjects.length > 0 && question.topics.length > 0 && (
              <span> • </span>
            )}
            {question.topics.length > 0 && (
              <span>
                Topic{question.topics.length > 1 ? "s" : ""}:{" "}
                {question.topics.map((t) => t.name).join(", ")}
              </span>
            )}
          </Text>
        </Box>
      )}
    </Stack>
  )
}
